import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, FileText, Mail, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import type { PublicEventSalesEvent } from '@/components/events/public-event-sales'
import type { TicketType } from '@/components/events/public-ticket-picker'
import { displayDate } from '@/lib/dates'
import { ApiError, postJSON } from '@/lib/api'

interface EventQuote {
  selection: {
    items: {
      ticket_type_id: number
      quantity: number
      subtotal: string
      discounted_amount: string
    }[]
    discount_cents: number
    discount: string
    tax_cents: number
    tax: string
    total_cents: number
    total: string
  }
}

export interface EventCheckoutOrder {
  id: number
  quantity: number
  buyer: { name: string; email: string }
  invoice: {
    number: number
    total: string
    due_on?: string
    payment_link_url?: string | null
  }
}

export function EventOrderSummary({
  eventId,
  selections,
  formId,
  paymentMethod = 'online',
  memberVerificationToken,
  discountCode,
  onDiscountCodeChange,
  onQuoteChange,
}: {
  eventId: number
  selections: { ticket: TicketType; quantity: number }[]
  formId: string
  paymentMethod?: 'online' | 'invoice'
  memberVerificationToken: string | null
  discountCode: string | null
  onDiscountCodeChange: (code: string | null) => void
  onQuoteChange: (totalCents: number | null) => void
}) {
  const quote = useEventCheckoutQuote({
    eventId,
    selections,
    discountCode,
    memberVerificationToken,
  })
  const lastValidSelection = React.useRef<EventQuote['selection'] | undefined>(undefined)
  if (quote.data?.selection) lastValidSelection.current = quote.data.selection
  const selection = quote.data?.selection ?? lastValidSelection.current
  const liveSelection = quote.data?.selection
  const quoteReady = Boolean(
    liveSelection && !quote.error && !quote.isPlaceholderData && !quote.isFetching,
  )
  React.useLayoutEffect(() => {
    onQuoteChange(quoteReady ? (liveSelection?.total_cents ?? null) : null)
  }, [onQuoteChange, quoteReady, liveSelection?.total_cents])
  return (
    <EventOrderSummaryCard
      selections={selections}
      formId={formId}
      paymentMethod={paymentMethod}
      discountCode={discountCode}
      selection={selection}
      quote={quote}
      quoteReady={quoteReady}
      onDiscountCodeChange={onDiscountCodeChange}
    />
  )
}

function EventOrderSummaryCard({
  selections,
  formId,
  paymentMethod,
  discountCode,
  selection,
  quote,
  quoteReady,
  onDiscountCodeChange,
}: {
  selections: { ticket: TicketType; quantity: number }[]
  formId: string
  paymentMethod: 'online' | 'invoice'
  discountCode: string | null
  selection: EventQuote['selection'] | undefined
  quote: ReturnType<typeof useEventCheckoutQuote>
  quoteReady: boolean
  onDiscountCodeChange: (code: string | null) => void
}) {
  return (
    <aside aria-labelledby="order-summary-heading">
      <Card className="sticky top-6 h-fit overflow-hidden">
        <CardHeader className="border-b border-border px-4 py-3">
          <CardTitle id="order-summary-heading" className="text-body-sm">
            Order summary
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 text-body-sm">
          {selections.map(({ ticket, quantity }) => {
            const quotedItem = selection?.items.find((item) => item.ticket_type_id === ticket.id)
            return (
              <div
                key={ticket.id}
                className="flex items-center justify-between gap-4 border-b border-border px-4 py-3"
              >
                <span>
                  {ticket.name} × {quantity}
                </span>
                <span className="shrink-0 tabular-nums">
                  {quotedItem?.subtotal ?? ticket.price}
                </span>
              </div>
            )
          })}
          <DiscountCodeControl
            code={discountCode}
            discount={selection?.discount}
            error={discountCodeError(quote.error)}
            applied={Boolean(discountCode && selection?.discount_cents && !quote.error)}
            onChange={onDiscountCodeChange}
          />
          {selection && selection.tax_cents > 0 && (
            <div className="flex items-center justify-between gap-4 border-b border-border px-4 py-3">
              <span>Tax</span>
              <span className="tabular-nums">{selection.tax}</span>
            </div>
          )}
          <div className="flex items-center justify-between gap-4 px-4 py-3 font-medium">
            <span>Total</span>
            <span className="shrink-0 tabular-nums">{selection?.total ?? 'Updating…'}</span>
          </div>
          <div className="border-t border-border px-4 py-3">
            <Button type="submit" form={formId} className="h-8 w-full" disabled={!quoteReady}>
              {checkoutButtonLabel(quoteReady ? selection : undefined, paymentMethod)}
            </Button>
          </div>
        </CardContent>
      </Card>
    </aside>
  )
}

function checkoutButtonLabel(
  selection: EventQuote['selection'] | undefined,
  paymentMethod: 'online' | 'invoice',
) {
  if ((selection?.total_cents ?? 0) > 0 && paymentMethod === 'online') {
    return `Pay ${selection?.total ?? 'Updating…'}`
  }
  return 'Complete registration'
}

function useEventCheckoutQuote({
  eventId,
  selections,
  discountCode,
  memberVerificationToken,
}: {
  eventId: number
  selections: { ticket: TicketType; quantity: number }[]
  discountCode: string | null
  memberVerificationToken: string | null
}) {
  const quantities = React.useMemo(
    () => Object.fromEntries(selections.map(({ ticket, quantity }) => [ticket.id, quantity])),
    [selections],
  )
  return useQuery({
    queryKey: ['public-event-checkout-quote', eventId, quantities, discountCode],
    queryFn: () =>
      postJSON<EventQuote>(`/api/v1/public/events/${eventId}/ticket_selection`, {
        quantities,
        ...(discountCode ? { discount_code: discountCode } : {}),
        ...(memberVerificationToken ? { member_verification_token: memberVerificationToken } : {}),
      }),
    placeholderData: (previous) => previous,
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  })
}

function DiscountCodeControl({
  code,
  discount,
  error,
  applied,
  onChange,
}: {
  code: string | null
  discount?: string
  error?: string
  applied: boolean
  onChange: (code: string | null) => void
}) {
  const [entering, setEntering] = React.useState(false)
  const [draft, setDraft] = React.useState('')
  const remove = () => {
    onChange(null)
    setEntering(false)
    setDraft('')
  }
  return (
    <div className="border-b border-border px-4 py-3">
      {applied ? (
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span>{code}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              aria-label="Remove discount"
              onClick={remove}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          <span className="tabular-nums">−{discount}</span>
        </div>
      ) : entering ? (
        <div>
          <form className="flex gap-2" onSubmit={(event) => applyDiscount(event, draft, onChange)}>
            <Input
              aria-label="Discount code"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              aria-invalid={Boolean(error)}
              className="h-8 min-w-0 flex-1"
            />
            <Button type="submit" variant="outline" className="h-8 px-3">
              Apply
            </Button>
          </form>
          {error && (
            <p className="pt-1 text-caption text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          className="h-8 border-dashed bg-transparent px-3 font-normal text-muted-foreground"
          onClick={() => setEntering(true)}
        >
          Add discount code
        </Button>
      )}
    </div>
  )
}

function discountCodeError(error: Error | null): string | undefined {
  if (!(error instanceof ApiError) || error.status !== 422) return undefined
  const body = error.body
  if (typeof body !== 'object' || body === null || !('errors' in body)) return undefined
  const errors = (body as { errors?: { discount_code?: unknown } }).errors?.discount_code
  return Array.isArray(errors) && typeof errors[0] === 'string' ? errors[0] : undefined
}

function applyDiscount(
  event: React.FormEvent,
  draft: string,
  onChange: (code: string | null) => void,
) {
  event.preventDefault()
  const code = draft.trim()
  if (code) onChange(code.toUpperCase())
}

export function PaymentPending() {
  return (
    <p className="text-body-sm text-muted-foreground" role="status">
      Confirming your payment…
    </p>
  )
}

export function RegistrationClosed() {
  return (
    <div className="space-y-1 px-4 py-6 text-center">
      <p className="text-body-sm font-medium">Registration closed</p>
      <p className="text-caption text-muted-foreground">
        This event page remains available for event details.
      </p>
    </div>
  )
}

export function PayLaterRegistrationSuccess({
  event,
  order,
}: {
  event: PublicEventSalesEvent
  order: EventCheckoutOrder
}) {
  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <header className="flex flex-col items-center space-y-1.5 text-center">
        <span className="mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
          <Check className="h-5 w-5 text-primary" aria-hidden="true" />
        </span>
        <h1 className="text-heading font-normal tracking-tight">You’re registered</h1>
        <p className="text-caption text-muted-foreground">
          Order #{order.id} · {event.name}
        </p>
      </header>
      <Card>
        <CardContent className="space-y-1 p-4">
          <p className="text-body-sm font-medium">{event.name}</p>
          <p className="text-caption text-muted-foreground">
            {event.schedule} {event.time_zone_label}
          </p>
          {event.location && <p className="text-caption text-muted-foreground">{event.location}</p>}
        </CardContent>
      </Card>
      <section className="rounded bg-muted/40 px-4 py-2 text-body-sm">
        <div className="flex items-center justify-between gap-4 py-1.5 font-medium">
          <span>Invoice #{order.invoice.number}</span>
          <span className="tabular-nums">{order.invoice.total}</span>
        </div>
      </section>
      <div className="space-y-3">
        <p className="flex items-start gap-2 text-caption text-muted-foreground">
          <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>Confirmation sent to {order.buyer.email}</span>
        </p>
        <p className="flex items-start gap-2 text-caption text-muted-foreground">
          <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            Invoice #{order.invoice.number} · due {displayDate(order.invoice.due_on ?? '')}
          </span>
        </p>
        {order.invoice.payment_link_url && (
          <Button asChild className="h-8 px-3">
            <a href={order.invoice.payment_link_url}>Pay now</a>
          </Button>
        )}
      </div>
    </div>
  )
}

export function PaidRegistrationSuccess({
  event,
  order,
}: {
  event: PublicEventSalesEvent
  order: EventCheckoutOrder
}) {
  return (
    <div className="mx-auto max-w-2xl space-y-6 lg:col-span-2">
      <header className="flex flex-col items-center space-y-1.5 text-center">
        <span className="mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
          <Check className="h-5 w-5 text-primary" aria-hidden="true" />
        </span>
        <h1 className="text-heading font-normal tracking-tight">You’re registered</h1>
        <p className="text-caption text-muted-foreground">
          Order #{order.id} · {event.name}
        </p>
      </header>
      <Card>
        <CardContent className="space-y-1 p-4">
          <p className="text-body-sm font-medium">{event.name}</p>
          <p className="text-caption text-muted-foreground">
            {event.schedule} {event.time_zone_label}
          </p>
          {event.location && <p className="text-caption text-muted-foreground">{event.location}</p>}
        </CardContent>
      </Card>
      <section className="rounded bg-muted/40 px-4 py-2 text-body-sm">
        <div className="flex items-center justify-between gap-4 py-1.5 font-medium">
          <span>Paid · Invoice #{order.invoice.number}</span>
          <span className="tabular-nums">{order.invoice.total}</span>
        </div>
      </section>
      <div className="space-y-3">
        <p className="flex items-start gap-2 text-caption text-muted-foreground">
          <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>Confirmation sent to {order.buyer.email}</span>
        </p>
        <p className="text-caption text-muted-foreground">
          Add or edit attendee names any time — the link is in your email.
        </p>
      </div>
    </div>
  )
}
